'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["_blur", "className", "children", "focused", "foregroundContent", "hovered", "href", "linkComponent", "onClick", "overlayClassName", "tabIndex", "use"],
  _excluded2 = ["hovered"],
  _excluded3 = ["focused"];
import PropTypes from 'prop-types';
import { cloneElement, Component } from 'react';
import classNames from 'classnames';
import devLogger from 'react-utils/devLogger';
import validHref from '../utils/propTypes/validHref';
import UIFlex from '../layout/UIFlex';
import UILink from '../link/UILink';
import UIClickable from '../button/UIClickable';
import { FOCUSABLE as FOCUSABLE_SELECTOR } from '../constants/Selectors';
import { hidden } from '../utils/propTypes/decorators';
import { warnIfFragment } from '../utils/devWarnings';
import * as CustomRenderer from '../utils/propTypes/customRenderer';
import HoverProvider from '../providers/HoverProvider';
import FocusProvider from '../providers/FocusProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const USE_CLASSES = {
  default: '',
  'full-card': 'private-hoverlay--full-card',
  'bottom-half-card': 'private-hoverlay--bottom-half-card'
};
class UIHoverlay extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      hasTabbableContent: false
    };
    this.refCallback = el => {
      if (!el) return;
      const hasTabbableContent = el.querySelector(FOCUSABLE_SELECTOR);
      this.setState({
        hasTabbableContent
      });
    };
  }
  render() {
    const _this$props = this.props,
      {
        _blur,
        className,
        children,
        foregroundContent,
        href,
        linkComponent,
        onClick,
        overlayClassName,
        tabIndex,
        use
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      hasTabbableContent
    } = this.state;
    if (process.env.NODE_ENV !== 'production') {
      if (href && onClick) {
        devLogger.warn({
          message: 'UIHoverlay: Cannot have both onClick and href props.'
        });
      }
    }
    warnIfFragment(children, UIHoverlay.displayName);
    return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, this.props, {
      children: hoverProviderProps => /*#__PURE__*/_jsx(FocusProvider, Object.assign({}, this.props, {
        children: focusProviderProps => {
          const {
              hovered
            } = hoverProviderProps,
            hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, _excluded2);
          const {
              focused
            } = focusProviderProps,
            focusProviderRestProps = _objectWithoutPropertiesLoose(focusProviderProps, _excluded3);
          const wrappedForegroundContent = foregroundContent == null ? null : /*#__PURE__*/_jsx("div", {
            className: classNames("uiOverlay-backdrop private-hoverlay__overlay private-overlay--contextual", overlayClassName, (href || onClick) && 'private-hoverlay__overlay--clickable', focused && 'private-hoverlay__overlay--focused', hovered && 'private-hoverlay__overlay--hovered'),
            children: /*#__PURE__*/_jsx(UIFlex, {
              align: "stretch",
              direction: "column",
              justify: "start",
              className: "private-overlay__overlay-content private-hoverlay__foreground-content",
              children: foregroundContent
            })
          });
          const renderedOverlay = /*#__PURE__*/_jsxs("span", {
            children: [wrappedForegroundContent, /*#__PURE__*/_jsx("div", {
              className: 'private-hoverlay__children' + (_blur ? " private-hoverlay__children--blur private-hoverlay--blur" : ""),
              children: /*#__PURE__*/cloneElement(children, {
                className: classNames('private-hoverlay__child', children.props.className)
              })
            })]
          });
          let renderedHoverlay;
          if (href) {
            renderedHoverlay = CustomRenderer.render(linkComponent, {
              children: renderedOverlay,
              className: 'display-block',
              href,
              use: 'unstyled'
            });
          } else if (onClick) {
            renderedHoverlay = /*#__PURE__*/_jsx(UIClickable, {
              className: "private-hoverlay__button",
              onClick: onClick,
              children: renderedOverlay
            });
          } else {
            renderedHoverlay = renderedOverlay;
          }
          let computedTabIndex;
          if (tabIndex != null) {
            computedTabIndex = tabIndex;
          } else {
            computedTabIndex = hasTabbableContent ? null : 0;
          }
          return /*#__PURE__*/_jsx("div", Object.assign({}, rest, hoverProviderRestProps, focusProviderRestProps, {
            className: classNames(className, 'private-hoverlay', USE_CLASSES[use], use === 'full-card' && 'is--module'),
            ref: this.refCallback,
            tabIndex: computedTabIndex,
            children: renderedHoverlay
          }));
        }
      }))
    }));
  }
}
UIHoverlay.propTypes = {
  _blur: PropTypes.bool,
  children: PropTypes.element,
  focused: hidden(PropTypes.bool),
  foregroundContent: PropTypes.node,
  hovered: PropTypes.bool,
  href: PropTypes.oneOfType([validHref]),
  linkComponent: CustomRenderer.propType,
  overlayClassName: PropTypes.string,
  use: PropTypes.oneOf(Object.keys(USE_CLASSES))
};
UIHoverlay.defaultProps = {
  _blur: true,
  linkComponent: UILink,
  use: 'default'
};
UIHoverlay.displayName = 'UIHoverlay';
export default UIHoverlay;